/**
 * RTWO Virtual Machine & Execution Sandbox — Domain Models
 * Phase 13.5 | Module ID: RTWO-VMX-001
 * Version: 1.0.0-phase13.5
 *
 * Strongly typed domain models for the RTWO Virtual Machine & Execution Sandbox.
 * Defines the deterministic execution layer for future smart contract execution.
 *
 * Truthful Data Policy:
 * - No fake executions, results, resource usage, or active opcodes.
 * - VM execution is NOT operational — architecture phase only.
 * - Smart contracts CANNOT run yet — runtime backend not connected.
 * - EVM compatibility is NOT claimed.
 * - All runtime values are null until real Execution Backend is established.
 *
 * Source: Virtual Machine Sandbox (RTWO-VMX-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type VMId = string;
export type ContextId = string;
export type SandboxId = string;
export type AccountingId = string;
export type DeterminismPolicyId = string;
export type InstructionId = string;
export type ResultId = string;
export type ViolationId = string;
export type VMXTimestamp = number | null;

export const VMX_ENGINE_VERSION = {
  moduleId: 'RTWO-VMX-001',
  moduleName: 'Virtual Machine & Execution Sandbox',
  version: '1.0.0-phase13.5',
  phase: 'Phase 13.5 — Virtual Machine & Execution Sandbox',
  status: 'Structure Complete — Waiting for Real Execution Backend',
  productionReadiness: 'Pending Runtime Backend and Execution Engine',
  policy:
    'Truthful Data Policy Enforced — No Fake Executions, Results, Resource Usage, or Active Opcodes',
} as const;

// ─────────────────────────────────────────────
// VM STATUS TYPES
// ─────────────────────────────────────────────

export type RTWOVMStatus =
  | 'architecture_ready'
  | 'sandbox_structure_complete'
  | 'runtime_backend_not_connected'
  | 'waiting_for_execution_engine'
  | 'production_pending'
  | 'operational'
  | 'maintenance'
  | 'deprecated';

export const VM_STATUS_LABELS: Record<RTWOVMStatus, string> = {
  architecture_ready: 'VM Architecture Ready',
  sandbox_structure_complete: 'Sandbox Structure Complete',
  runtime_backend_not_connected: 'Runtime Backend Not Connected',
  waiting_for_execution_engine: 'Waiting for Execution Engine',
  production_pending: 'Production Pending',
  operational: 'Operational',
  maintenance: 'Maintenance',
  deprecated: 'Deprecated',
};

export type RTWOExecutionMode = 'deterministic' | 'sandboxed' | 'isolated' | 'not_configured';

export type RTWORuntimeCompatibility =
  'rtwo_native_vm' | 'wasm_compatible' | 'custom_bytecode' | 'not_configured';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOVirtualMachine
// ─────────────────────────────────────────────

export interface RTWOVirtualMachine {
  vmId: VMId;
  vmName: string;
  vmVersion: string;
  runtimeCompatibility: RTWORuntimeCompatibility;
  executionMode: RTWOExecutionMode;
  status: RTWOVMStatus;
  createdAt: VMXTimestamp;
}

// ─────────────────────────────────────────────
// EXECUTION CONTEXT STATUS
// ─────────────────────────────────────────────

export type RTWOContextStatus =
  | 'architecture_ready'
  | 'not_created'
  | 'context_created'
  | 'sandbox_prepared'
  | 'validated'
  | 'executing'
  | 'result_generated'
  | 'state_applied'
  | 'completed'
  | 'reverted'
  | 'failed';

export const CONTEXT_STATUS_LABELS: Record<RTWOContextStatus, string> = {
  architecture_ready: 'Architecture Ready',
  not_created: 'Not Created',
  context_created: 'Context Created',
  sandbox_prepared: 'Sandbox Prepared',
  validated: 'Validated',
  executing: 'Executing',
  result_generated: 'Result Generated',
  state_applied: 'State Applied',
  completed: 'Completed',
  reverted: 'Reverted',
  failed: 'Failed',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOExecutionContext
// ─────────────────────────────────────────────

export interface RTWOExecutionContext {
  contextId: ContextId;
  contractId: string;
  callerReference: string;
  transactionReference: string;
  stateReference: string;
  resourcePolicyId: string;
  status: RTWOContextStatus;
  createdAt: VMXTimestamp;
}

// ─────────────────────────────────────────────
// SANDBOX TYPES
// ─────────────────────────────────────────────

export type RTWOIsolationLevel =
  'process_isolated' | 'memory_isolated' | 'full_isolated' | 'not_configured';

export type RTWOStorageAccessPolicy =
  'read_only' | 'write_restricted' | 'full_access' | 'no_access' | 'not_configured';

export type RTWOSandboxStatus =
  | 'architecture_ready'
  | 'structure_complete'
  | 'not_provisioned'
  | 'provisioning'
  | 'ready'
  | 'active'
  | 'terminated'
  | 'failed';

export const SANDBOX_STATUS_LABELS: Record<RTWOSandboxStatus, string> = {
  architecture_ready: 'Architecture Ready',
  structure_complete: 'Structure Complete',
  not_provisioned: 'Not Provisioned',
  provisioning: 'Provisioning',
  ready: 'Ready',
  active: 'Active',
  terminated: 'Terminated',
  failed: 'Failed',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOExecutionSandbox
// ─────────────────────────────────────────────

export interface RTWOExecutionSandbox {
  sandboxId: SandboxId;
  contextId: ContextId;
  isolationLevel: RTWOIsolationLevel;
  memoryLimitReference: string;
  computeLimitReference: string;
  storageAccessPolicy: RTWOStorageAccessPolicy;
  status: RTWOSandboxStatus;
}

// ─────────────────────────────────────────────
// RESOURCE ACCOUNTING TYPES
// ─────────────────────────────────────────────

export type RTWOAccountingStatus =
  'architecture_ready' | 'not_configured' | 'pending' | 'active' | 'settled' | 'failed';

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOResourceAccounting
// ─────────────────────────────────────────────

export interface RTWOResourceAccounting {
  accountingId: AccountingId;
  contextId: ContextId;
  computeUnitsReference: string;
  memoryUnitsReference: string;
  storageUnitsReference: string;
  feeReference: string;
  status: RTWOAccountingStatus;
}

// ─────────────────────────────────────────────
// DETERMINISM POLICY TYPES
// ─────────────────────────────────────────────

export type RTWOTimeAccessPolicy = 'block_timestamp_only' | 'no_time_access' | 'not_configured';

export type RTWORandomnessPolicy =
  'deterministic_seed_only' | 'no_randomness' | 'vrf_based' | 'not_configured';

export type RTWOExternalCallPolicy = 'no_external_calls' | 'whitelisted_only' | 'not_configured';

export type RTWODeterminismPolicyStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWODeterminismPolicy
// ─────────────────────────────────────────────

export interface RTWODeterminismPolicy {
  policyId: DeterminismPolicyId;
  allowedOperations: string[];
  forbiddenOperations: string[];
  timeAccessPolicy: RTWOTimeAccessPolicy;
  randomnessPolicy: RTWORandomnessPolicy;
  externalCallPolicy: RTWOExternalCallPolicy;
  status: RTWODeterminismPolicyStatus;
}

// ─────────────────────────────────────────────
// RUNTIME INSTRUCTION TYPES
// ─────────────────────────────────────────────

export type RTWOInstructionType =
  | 'arithmetic'
  | 'memory'
  | 'storage'
  | 'control_flow'
  | 'cryptographic'
  | 'state_access'
  | 'event_emission'
  | 'inter_contract'
  | 'system';

export type RTWOInstructionStatus =
  'architecture_defined' | 'not_implemented' | 'waiting_for_runtime' | 'implemented' | 'deprecated';

export const INSTRUCTION_STATUS_LABELS: Record<RTWOInstructionStatus, string> = {
  architecture_defined: 'Architecture Defined',
  not_implemented: 'Not Implemented',
  waiting_for_runtime: 'Waiting for Runtime',
  implemented: 'Implemented',
  deprecated: 'Deprecated',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWORuntimeInstruction
// ─────────────────────────────────────────────

export interface RTWORuntimeInstruction {
  instructionId: InstructionId;
  opcode: string;
  instructionType: RTWOInstructionType;
  resourceCostReference: string;
  status: RTWOInstructionStatus;
}

// ─────────────────────────────────────────────
// EXECUTION RESULT TYPES
// ─────────────────────────────────────────────

export type RTWOExecutionResultStatus =
  | 'architecture_ready'
  | 'no_executions'
  | 'success'
  | 'reverted'
  | 'failed'
  | 'waiting_for_backend';

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWOExecutionResult
// ─────────────────────────────────────────────

export interface RTWOExecutionResult {
  resultId: ResultId;
  contextId: ContextId;
  outputReference: string;
  stateChangesReference: string;
  emittedEventsReference: string;
  executionStatus: RTWOExecutionResultStatus;
  createdAt: VMXTimestamp;
}

// ─────────────────────────────────────────────
// SANDBOX VIOLATION TYPES
// ─────────────────────────────────────────────

export type RTWOViolationType =
  | 'memory_limit_exceeded'
  | 'compute_limit_exceeded'
  | 'storage_access_violation'
  | 'forbidden_operation'
  | 'external_call_attempt'
  | 'time_access_violation'
  | 'randomness_violation'
  | 'determinism_violation';

export type RTWOViolationSeverity = 'low' | 'medium' | 'high' | 'critical';

export type RTWOViolationActionTaken =
  | 'execution_halted'
  | 'sandbox_terminated'
  | 'transaction_reverted'
  | 'penalty_applied'
  | 'logged_only';

export type RTWOViolationStatus =
  'architecture_ready' | 'no_violations' | 'active' | 'resolved' | 'escalated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOSandboxViolation
// ─────────────────────────────────────────────

export interface RTWOSandboxViolation {
  violationId: ViolationId;
  contextId: ContextId;
  violationType: RTWOViolationType;
  severity: RTWOViolationSeverity;
  actionTaken: RTWOViolationActionTaken;
  status: RTWOViolationStatus;
  createdAt: VMXTimestamp;
}

// ─────────────────────────────────────────────
// EXECUTION LIFECYCLE
// ─────────────────────────────────────────────

export interface ExecutionLifecycleStage {
  stage: RTWOContextStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealData: boolean;
  truthfulNote: string;
}

export const VM_EXECUTION_LIFECYCLE: ExecutionLifecycleStage[] = [
  {
    stage: 'not_created',
    label: 'Requested',
    description:
      'Execution request received. Contract ID, caller reference, and transaction reference recorded.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Execution Backend. No fake executions created.',
  },
  {
    stage: 'context_created',
    label: 'Context Created',
    description:
      'Execution context created. Resource policy assigned. State reference established.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real VM Context Manager. No fake contexts.',
  },
  {
    stage: 'sandbox_prepared',
    label: 'Sandbox Prepared',
    description:
      'Execution sandbox provisioned. Isolation level applied. Memory and compute limits enforced.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Sandbox Manager. No fake sandbox provisioning.',
  },
  {
    stage: 'validated',
    label: 'Validated',
    description:
      'Execution request validated against determinism policy. Forbidden operations checked. Resource policy verified.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Determinism Policy Service. No fake validation.',
  },
  {
    stage: 'executing',
    label: 'Executing',
    description:
      'VM is executing instructions. Resource accounting active. Sandbox monitoring active.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real VM Execution Engine. No fake execution results.',
  },
  {
    stage: 'result_generated',
    label: 'Result Generated',
    description: 'Execution result generated. Output reference recorded. Emitted events captured.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Execution Result Service. No fake outputs.',
  },
  {
    stage: 'state_applied',
    label: 'State Applied',
    description:
      'State changes applied to Ledger Engine. Storage updates committed. Events published to Event Bus.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Ledger Engine connection. No fake state changes.',
  },
  {
    stage: 'completed',
    label: 'Completed',
    description:
      'Execution completed successfully. All state changes committed. Resource accounting settled.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal success state. Requires real Ledger Engine confirmation.',
  },
  {
    stage: 'reverted',
    label: 'Reverted',
    description:
      'Execution reverted. State changes rolled back. Revert reason recorded. Partial fees may apply.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal revert state. Revert reason must come from real VM.',
  },
  {
    stage: 'failed',
    label: 'Failed',
    description:
      'Execution failed due to VM error or sandbox violation. State changes rolled back. Error details recorded.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal failure state. Error details must come from real backend.',
  },
];

// ─────────────────────────────────────────────
// PREDEFINED RUNTIME INSTRUCTIONS (Architecture Only)
// ─────────────────────────────────────────────

export const PREDEFINED_INSTRUCTIONS: RTWORuntimeInstruction[] = [
  {
    instructionId: 'instr-001',
    opcode: 'ADD',
    instructionType: 'arithmetic',
    resourceCostReference: 'cost-ref-arithmetic-low',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-002',
    opcode: 'SUB',
    instructionType: 'arithmetic',
    resourceCostReference: 'cost-ref-arithmetic-low',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-003',
    opcode: 'MUL',
    instructionType: 'arithmetic',
    resourceCostReference: 'cost-ref-arithmetic-medium',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-004',
    opcode: 'DIV',
    instructionType: 'arithmetic',
    resourceCostReference: 'cost-ref-arithmetic-medium',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-005',
    opcode: 'MLOAD',
    instructionType: 'memory',
    resourceCostReference: 'cost-ref-memory-low',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-006',
    opcode: 'MSTORE',
    instructionType: 'memory',
    resourceCostReference: 'cost-ref-memory-medium',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-007',
    opcode: 'SLOAD',
    instructionType: 'storage',
    resourceCostReference: 'cost-ref-storage-high',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-008',
    opcode: 'SSTORE',
    instructionType: 'storage',
    resourceCostReference: 'cost-ref-storage-very-high',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-009',
    opcode: 'JUMP',
    instructionType: 'control_flow',
    resourceCostReference: 'cost-ref-control-low',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-010',
    opcode: 'JUMPI',
    instructionType: 'control_flow',
    resourceCostReference: 'cost-ref-control-low',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-011',
    opcode: 'RETURN',
    instructionType: 'control_flow',
    resourceCostReference: 'cost-ref-control-low',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-012',
    opcode: 'REVERT',
    instructionType: 'control_flow',
    resourceCostReference: 'cost-ref-control-low',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-013',
    opcode: 'HASH256',
    instructionType: 'cryptographic',
    resourceCostReference: 'cost-ref-crypto-medium',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-014',
    opcode: 'VERIFY_SIG',
    instructionType: 'cryptographic',
    resourceCostReference: 'cost-ref-crypto-high',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-015',
    opcode: 'SSTATE',
    instructionType: 'state_access',
    resourceCostReference: 'cost-ref-state-medium',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-016',
    opcode: 'EMIT_EVENT',
    instructionType: 'event_emission',
    resourceCostReference: 'cost-ref-event-medium',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-017',
    opcode: 'CALL',
    instructionType: 'inter_contract',
    resourceCostReference: 'cost-ref-call-high',
    status: 'architecture_defined',
  },
  {
    instructionId: 'instr-018',
    opcode: 'SYSCALL',
    instructionType: 'system',
    resourceCostReference: 'cost-ref-system-high',
    status: 'architecture_defined',
  },
];

// ─────────────────────────────────────────────
// VMX EVENT TYPES
// ─────────────────────────────────────────────

export type VMXEventType =
  | 'vm_registered'
  | 'context_created'
  | 'sandbox_prepared'
  | 'execution_started'
  | 'execution_completed'
  | 'execution_reverted'
  | 'execution_failed'
  | 'violation_detected'
  | 'resource_limit_exceeded'
  | 'result_generated';

export type VMXEventHandler = (event: {
  eventType: VMXEventType;
  payload: Record<string, unknown>;
}) => void;
